import React from 'react';
import { observer } from 'mobx-react-lite';
import { TrashIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { DeleteDialog } from '../../../components/dialogs/DeleteDialog';
import { InlineEditableText } from '../../../components/editable-text/inline-editable-text';
import { ColorPicker } from './color-picker.component';
import { Strip, Wrapper } from '../../tasks/components/styled-tasks-components';
import { openModal } from '../../../core/modal/open-modal';

import type { Tag } from '../../../app/entities/tag';

import styles from './tag-item.module.scss';
type TagItemProps = {
  tag: Tag;
};

export const TagItem: React.FC<TagItemProps> = observer(({ tag }) => {
  const { t } = useTranslation('video');

  const menuItems: React.ComponentProps<typeof DropdownMenu>['items'] = [
    {
      title: 'Delete tag',
      icon: <TrashIcon />,
      type: 'danger' as const,
      onSelect: () =>
        openModal(DeleteDialog, {
          title: 'Delete tag',
          text: `The tag and its references will be deleted completely from the space.`,
          onSubmit: () => tag.delete(),
        }),
    },
  ];

  const titleValidatorSchema = yup.string().max(24, t('common:fieldMax', { max: 24 }));

  const getUsagePlaceholder = () => {
    const usage = tag.usage;
    const target = tag.target;

    if (!usage) return 'Not used';
    return usage > 1 ? `${usage} ${target}s` : `${usage} ${target}`;
  };

  return (
    <Strip>
      <Wrapper data-full-width="true">
        <ColorPicker colorHex={tag.color} onChange={(color) => tag.update({ color })} />
      </Wrapper>

      <span style={{ display: 'flex', flexShrink: '1', flexGrow: '1', minWidth: 0 }}>
        <InlineEditableText
          validationType="onChange"
          maxLength={25}
          schema={titleValidatorSchema}
          value={tag.text}
          onSubmit={(text) => tag.update({ text: text || 'No tag name' })}
          isBorderless
        />
      </span>

      <div className={styles['right']}>
        <div>{getUsagePlaceholder()}</div>
        <DropdownMenu items={menuItems} />
      </div>
    </Strip>
  );
});
